import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  Edit,
  Delete,
  Add,
  Refresh,
  Payment,
  DragIndicator,
  ToggleOn,
  ToggleOff,
  Sort,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  fetchPaymentMethods,
  togglePaymentMethodStatus,
  deletePaymentMethod,
  reorderPaymentMethods,
  setFilters,
  clearFilters,
  setPage,
} from '../store/slices/paymentMethodsSlice';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import PaymentMethodFormModal from '../components/paymentMethods/PaymentMethodFormModal';
import { PaymentMethodData } from '../types';

const PaymentMethodsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { paymentMethods, pagination, loading, error } = useSelector(
    (state: RootState) => state.paymentMethods
  );
  
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodData | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchPaymentMethods({ page: 1, limit: 20 }) as any);
  }, [dispatch]);

  const handleSearch = () => {
    const filters: any = {};
    if (searchTerm) filters.search = searchTerm;
    if (typeFilter) filters.type = typeFilter;
    if (statusFilter) filters.isActive = statusFilter === 'active';
    dispatch(setFilters(filters));
    dispatch(fetchPaymentMethods({ page: 1, limit: 20, filters }) as any);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    dispatch(fetchPaymentMethods({ page: newPage + 1, limit: 20 }) as any);
  };

  const handleRefresh = () => {
    dispatch(fetchPaymentMethods({ page: 1, limit: 20 }) as any);
  };

  const handleCreate = () => {
    setFormMode('create');
    setSelectedPaymentMethod(null);
    setShowFormModal(true);
  };

  const handleEdit = (paymentMethod: PaymentMethodData) => {
    setFormMode('edit');
    setSelectedPaymentMethod(paymentMethod);
    setShowFormModal(true);
  };

  const handleToggleStatus = async (paymentMethod: PaymentMethodData) => {
    try {
      await dispatch(togglePaymentMethodStatus(paymentMethod.id) as any);
      toast.success(`Payment method ${paymentMethod.isActive ? 'deactivated' : 'activated'} successfully`);
    } catch (error) {
      toast.error('Failed to toggle payment method status');
    }
  };

  const handleDelete = async () => {
    if (!selectedPaymentMethod) return;
    
    setDeleteLoading(true);
    try {
      await dispatch(deletePaymentMethod(selectedPaymentMethod.id) as any);
      toast.success('Payment method deleted successfully');
      setShowDeleteDialog(false);
      setSelectedPaymentMethod(null);
    } catch (error) {
      toast.error('Failed to delete payment method');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleReorder = async () => {
    const reorderData = {
      order: paymentMethods.map((method, index) => ({
        id: method.id,
        sortOrder: index + 1,
      })),
    };
    
    try {
      await dispatch(reorderPaymentMethods(reorderData) as any);
      toast.success('Payment methods reordered successfully');
    } catch (error) {
      toast.error('Failed to reorder payment methods');
    }
  };

  const getTypeLabel = (type: string) => {
    const typeMap: { [key: string]: string } = {
      bank: 'Bank',
      mobile_money: 'Mobile Money',
      crypto: 'Cryptocurrency',
      other: 'Other',
    };
    return typeMap[type] || type;
  };

  const getTypeColor = (type: string) => {
    const colorMap: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'error' } = {
      bank: 'primary',
      mobile_money: 'secondary',
      crypto: 'warning',
      other: 'error',
    };
    return colorMap[type] || 'default';
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5,
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          Payment Methods
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          disabled={loading}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 2, mb: { xs: 2, sm: 3 } }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Payment sx={{ mr: 2, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">{paymentMethods.length}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Payment Methods
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ToggleOn sx={{ mr: 2, color: 'success.main' }} />
              <Box>
                <Typography variant="h6">
                  {paymentMethods.filter(m => m.isActive).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Methods
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ToggleOff sx={{ mr: 2, color: 'error.main' }} />
              <Box>
                <Typography variant="h6">
                  {paymentMethods.filter(m => !m.isActive).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Inactive Methods
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' }, mb: 2 }}>
          <TextField
            placeholder="Search payment methods..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
            size="small"
          />
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 1, 
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            {/* First row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button variant="contained" onClick={handleSearch} size="small" sx={{ flex: { xs: 1, sm: 'none' } }}>
                Search
              </Button>
            </Box>
            
            {/* Second row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Filters
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreate}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Add 
              </Button>
            </Box>
          </Box>
        </Box>

        {showFilters && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="bank">Bank</MenuItem>
                <MenuItem value="mobile_money">Mobile Money</MenuItem>
                <MenuItem value="crypto">Cryptocurrency</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Payment Methods Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table sx={{ minWidth: { xs: 520, md: 800 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Icon</TableCell>
                <TableCell>Name</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Account Number</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Verify</TableCell>
                <TableCell>Type</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Description</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Sort Order</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paymentMethods.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No payment methods found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paymentMethods.map((paymentMethod) => (
                  <TableRow key={paymentMethod.id} hover>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="h6">{paymentMethod.icon}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {paymentMethod.name}
                        </Typography>
                        <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="textSecondary">
                            {paymentMethod.accountNumber}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {paymentMethod.createdAt ? dayjs(paymentMethod.createdAt).format('MMM DD, YYYY') : '-'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2" fontFamily="monospace">
                        {paymentMethod.accountNumber}
                      </Typography>
                      {paymentMethod.type === 'crypto' && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {paymentMethod.binanceIdType === 'binance_id' ? 'Binance UID' : 'Binance Pay ID'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      {paymentMethod.type === 'crypto' ? (
                        <Box>
                          <Typography variant="body2" fontWeight="bold" color="warning.main">
                            1 USDT = {paymentMethod.exchangeRate || 135} ETB
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {paymentMethod.network || 'Binance Pay'}
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          <Typography variant="body2">
                            {paymentMethod.verifyAccountName || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {paymentMethod.justVerifyProvider && paymentMethod.justVerifyProvider !== 'none'
                              ? paymentMethod.justVerifyProvider.toUpperCase()
                              : 'Off'}
                          </Typography>
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          label={getTypeLabel(paymentMethod.type)}
                          color={getTypeColor(paymentMethod.type)}
                          size="small"
                          sx={{ width: 'fit-content', minWidth: '80px' }}
                        />
                        <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="textSecondary">
                            {paymentMethod.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {paymentMethod.description}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DragIndicator sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {paymentMethod.sortOrder}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          label={paymentMethod.isActive ? 'Active' : 'Inactive'}
                          color={paymentMethod.isActive ? 'success' : 'default'}
                          size="small"
                          sx={{ width: 'fit-content', minWidth: '80px' }}
                        />
                        <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <DragIndicator sx={{ color: 'text.secondary', fontSize: '0.75rem' }} />
                            <Typography variant="caption" color="textSecondary">
                              Order: {paymentMethod.sortOrder}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body2">
                        {paymentMethod.createdAt ? dayjs(paymentMethod.createdAt).format('MMM DD, YYYY') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Tooltip title="Toggle Status">
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(paymentMethod)}
                            color={paymentMethod.isActive ? 'success' : 'default'}
                          >
                            {paymentMethod.isActive ? <ToggleOn /> : <ToggleOff />}
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(paymentMethod)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedPaymentMethod(paymentMethod);
                              setShowDeleteDialog(true);
                            }}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[10, 20, 50]}
          onRowsPerPageChange={(e) => {
            const newLimit = parseInt(e.target.value, 10);
            dispatch(fetchPaymentMethods({ page: 1, limit: newLimit }) as any);
          }}
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexDirection: 'row',
              gap: { xs: 0.5, sm: 1 },
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            },
            '& .MuiTablePagination-spacer': {
              display: 'none',
            },
            '& .MuiTablePagination-selectLabel': {
              display: 'none',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 2, sm: 0 },
            },
            '& .MuiTablePagination-select': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 1, sm: 0 },
            },
            '& .MuiTablePagination-actions': {
              order: { xs: 3, sm: 0 },
            },
          }}
        />
      </Paper>

      {/* Form Modal */}
      <PaymentMethodFormModal
        open={showFormModal}
        onClose={() => setShowFormModal(false)}
        paymentMethod={selectedPaymentMethod}
        mode={formMode}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Payment Method</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete "{selectedPaymentMethod?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentMethodsPage; 